<template>
  <div>
    <!-- Toolbar -->
    <v-toolbar color="blue darken-3 mx-3" dark dense flat>
      <v-btn small icon @click="$router.back()" class="mr-1">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon class="mr-2">mdi-receipt-text-plus-outline</v-icon>
      <v-toolbar-title class="font-weight-bold subtitle-1">Generar Recibo Manual</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-chip v-if="cajaabierta" small color="green lighten-1" class="mr-2">
        <v-icon small left>mdi-lock-open-outline</v-icon> Caja abierta
      </v-chip>
      <v-chip v-else-if="!loadingverificacion" small color="red lighten-1">
        <v-icon small left>mdi-lock-outline</v-icon> Sin caja
      </v-chip>
    </v-toolbar>

    <v-container class="px-4 pt-3" style="max-width:1280px">

      <!-- Aviso -->
      <v-alert dense text type="info" class="mb-3" style="font-size:13px">
        Los campos con <strong>*</strong> son obligatorios. Los demás pueden estar vacíos o contener al menos 5 caracteres.
        Estos recibos <strong>no están vinculados</strong> al plan de pagos del estudiante.
      </v-alert>

      <form @submit="validar">
        <!-- Campos del formulario -->
        <v-card outlined class="mb-3">
          <v-card-title class="py-2 px-3 blue lighten-5 subtitle-2 blue--text text--darken-3">
            <v-icon small left color="blue darken-3">mdi-form-select</v-icon>
            Datos del recibo
          </v-card-title>
          <v-card-text class="pt-3 pb-2">
            <v-row dense>

              <!-- Columna 1 -->
              <v-col cols="12" sm="6" md="4">

                <!-- CI Estudiante -->
                <div class="mb-3">
                  <div class="caption grey--text text--darken-1 mb-1">
                    <strong class="red--text">*</strong> CI Estudiante
                  </div>
                  <div class="d-flex align-center" style="gap:6px">
                    <v-text-field
                      v-model.trim="filtroestudiante"
                      label="Número de CI"
                      placeholder="Ej: 12345678"
                      :loading="isloadingestudent"
                      :disabled="isloadingestudent"
                      hide-details outlined dense
                      @keyup.enter="buscarEstudiante" />
                    <v-btn
                      color="primary"
                      :loading="isloadingestudent"
                      :disabled="isloadingestudent"
                      @click="buscarEstudiante"
                      small style="min-width:40px;height:40px">
                      <v-icon>mdi-magnify</v-icon>
                    </v-btn>
                  </div>
                  <v-chip v-if="estudianteEncontrado" color="green lighten-5" class="green--text text--darken-2 mt-1" small>
                    <v-icon left small>mdi-check-circle</v-icon>
                    {{ input.estudiante }}
                  </v-chip>
                  <div v-if="estudianteNoEncontrado">
                    <v-alert type="warning" dense text class="mt-2 mb-1" style="font-size:13px">
                      No se encontró estudiante con CI "{{ filtroestudiante }}". Ingrese el nombre manualmente:
                    </v-alert>
                    <v-text-field
                      v-model.trim="input.estudiante"
                      label="Nombre completo del estudiante"
                      placeholder="Ingrese el nombre completo..."
                      outlined dense hide-details />
                  </div>
                </div>

                <v-text-field
                  v-model.trim="input.lugar"
                  label="* Lugar"
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada"
                  required />

                <v-text-field
                  v-model.number="input.monto"
                  label="* Monto (Bs.)"
                  type="number" step="0.01"
                  placeholder="0.00"
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada"
                  required />

                <v-text-field
                  v-model.trim="input.num_doc"
                  label="Carnet"
                  outlined dense
                  :hint="cimsg"
                  :persistent-hint="!!cimsg"
                  class="mb-2"
                  :disabled="!busquedaRealizada" />

                <v-text-field
                  v-model.trim="input.direccion"
                  label="Dirección"
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada" />
              </v-col>

              <!-- Columna 2 -->
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model.trim="input.celular"
                  label="Celular"
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada" />

                <v-text-field
                  v-model.trim="input.nro_factura"
                  label="Nro. Factura"
                  placeholder="de al menos 5 dígitos..."
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada" />

                <v-textarea
                  v-model.trim="input.concepto"
                  label="* Concepto"
                  rows="3" outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada"
                  required />

                <v-textarea
                  v-model.trim="input.forma_pago"
                  label="* Forma de pago"
                  rows="3" outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada"
                  required />

                <v-text-field
                  v-model.number="input.saldo"
                  label="Saldo (Bs.)"
                  type="number"
                  placeholder="en bolivianos..."
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada" />

                <v-text-field
                  v-model.trim="input.num_cuota"
                  label="Nro. Cuota"
                  placeholder="puede separar por comas..."
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada" />
              </v-col>

              <!-- Columna 3 -->
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model.trim="input.proximo_pago"
                  label="Próximo pago"
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada" />

                <v-text-field
                  v-model.trim="input.emisor"
                  label="* Emisor"
                  outlined dense hide-details
                  readonly
                  class="mb-2"
                  :disabled="!busquedaRealizada"
                  required />

                <v-text-field
                  v-model.trim="input.asesor"
                  label="Asesor"
                  outlined dense hide-details
                  class="mb-2"
                  :disabled="!busquedaRealizada" />

                <v-text-field
                  v-model.trim="input.fecha_pago"
                  label="Fecha de emisión"
                  outlined dense hide-details
                  :disabled="!busquedaRealizada"
                  class="mb-2" />

                <v-select
                  :items="xsedes"
                  v-model="input.sede_destino"
                  item-text="nombre"
                  item-value="id"
                  label="* Sede destino"
                  outlined dense hide-details
                  placeholder="Seleccione..."
                  class="mb-2"
                  :disabled="!busquedaRealizada"
                  return-object />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Preview del recibo -->
        <v-card outlined class="mb-3">
          <v-card-title class="py-2 px-3 blue lighten-5 subtitle-2 blue--text text--darken-3">
            <v-icon small left color="blue darken-3">mdi-eye-outline</v-icon>
            Vista previa del recibo
          </v-card-title>
          <v-card-text class="pa-3">
            <div id="content-preview">
              <div style="width:10px!important;height:10px!important;overflow:hidden">
                <canvas id="canvasrecibo" width="1280" height="1080"></canvas>
              </div>
              <div class="center-block" style="width:100%;height:640px">
                <canvas id="myCanvasview" style="border:1px solid #e0e0e0;width:100%;height:100%;border-radius:4px"></canvas>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Error y acciones -->
        <v-alert v-if="msg" dense type="error" outlined class="mb-3" style="white-space:pre-line">
          {{ msg }}
        </v-alert>

        <v-progress-linear v-if="generando" indeterminate color="blue darken-3" class="mb-3" />

        <v-alert v-if="!cajaabierta && !loadingverificacion" dense text type="warning" class="mb-3" style="font-size:13px">
          <v-icon left small>mdi-lock-outline</v-icon>
          No tienes una caja abierta. Para generar recibos debes <strong>aperturar una caja</strong> primero.
        </v-alert>

        <div class="d-flex align-center justify-center mb-6" style="gap:12px">
          <v-btn
            :disabled="generando || generado || !cajaabierta"
            :loading="generando"
            type="submit"
            color="blue darken-3"
            dark rounded>
            <v-icon left>mdi-printer</v-icon>
            Generar y descargar PDF
          </v-btn>
          <v-btn
            :disabled="generando"
            type="button"
            color="red darken-1"
            dark rounded outlined
            @click="limpiarDatos()">
            <v-icon left>mdi-brush</v-icon>
            Limpiar datos
          </v-btn>
        </div>
      </form>

    </v-container>

    <ABuscarPersona ref="abuscarpersona" />
  </div>
</template>

<script>
import gql from 'graphql-tag';
import ABuscarPersona from '@/components/ABuscarPersona';
import xaxios from '@/xaxios';

export default {
  data(){
    return{
        usuario:{usuario:{}},
        fondo:null,
        firma:null,
        generando: false,
        generado: false,
        msg:'',
        cimsg:'',
        numero: '',
        timeoutHnd: 0,
        filtroestudiante: '',
        isloadingestudent: false,
        estudianteEncontrado: false,
        estudianteNoEncontrado: false,
        firma_path: process.env.VUE_APP_URL_BG_FIRMA_USER,
        estudiantes: [],
        xsedes:[],
        loadingverificacion: true,
        cajaabierta: false,
        busquedaRealizada: false,
        input:{
            lugar: '',
            monto: null,
            estudiante: '',
            num_doc: '',
            telefono: '',
            celular: '',
            concepto: '',
            forma_pago: '',
            saldo: null,
            num_cuota: '',
            direccion: '',
            zona: '',
            proximo_pago: '',
            asesor: '',
            emisor: '',
            recibo_fondo: '',
            sede_id: -1,
            fecha_pago: '',
            nro_factura: '',
            sede_destino: null,
        }
    }
  },
  components:{ABuscarPersona},
  validators: {},
  watch:{
      '$store.state.DATA_USER.nombres': function (){ this.getcurrentusuario(); this.verificar() },
      '$store.state.CARGO.idsede': function (){ this.getcurrentusuario(); this.verificar(); },
      '$store.state.cajaperturada': function (){ this.verificar(); },
      'input':{
          deep: true,
          handler(){ this.cargardatos(); }
      },
      filtroestudiante() {
        this.estudianteEncontrado = false;
        this.estudianteNoEncontrado = false;
        this.busquedaRealizada = false;
        this.input.estudiante = '';
        this.input.num_doc = '';
        this.cargardatos();
      },
  },
  methods:{
      fechahoy(){
          let date = new Date();
          let d = date.getDate();
          let m = date.getMonth()+1;
          let a = date.getFullYear();
          if( d < 10 ) d = '0'+d;
          if( m < 10 ) m = '0'+m;
          this.input.fecha_pago = d+'/'+m+'/'+a;
      },
      fechaproximopago(){
          let date = new Date();
          date.setDate( 15 );
          date.setMonth( date.getMonth()+1 );
          let d = date.getDate();
          let m = date.getMonth()+1;
          let a = date.getFullYear();
          if( d < 10 ) d = '0'+d;
          if( m < 10 ) m = '0'+m;
          this.input.proximo_pago = d+'/'+m+'/'+a;
      },
      getcurrentusuario(){
          let user = this.$store.state.DATA_USER;
          this.input.sede_id = this.$store.state.CARGO.idsede;
          if( user ){
              this.usuario = user;
              if( !user.seg_apellido ) user.seg_apellido = '';
              this.input.asesor = (user.nombres+' '+user.pri_apellido+' '+user.seg_apellido).trim();
              this.input.emisor = (user.nombres+' '+user.pri_apellido+' '+user.seg_apellido).trim();
              if( this.usuario.usuario ) this.cargarfondo();
          }
      },
      voltearfecha( f ){
          if( !f ) return '';
          let partes = f.split('/');
          return partes[2]+'-'+partes[1]+'-'+partes[0];
      },
      validarextensionci(){},
      async verificar(){
        this.msg = '';
        this.loadingverificacion = true;
        this.cajaabierta = false;

        const sedeid = this.$store.state.CARGO.sede.id;
        const userid = this.$store.state.DATA_USER.id;
        const url = `${process.env.VUE_APP_API_REST}/api/v1/esam/cajas/abierto/${sedeid}/usuario/${userid}`;
        const res = await xaxios.get(url).then(x=>x).catch(x=>x);
        if(res && res.data){
            const data = res.data;
            window.console.log('verif caja:', data);
            this.cajaabierta = true;
        }else{
            this.$root.$notify.show(`Apertura una caja para poder generar recibos.`, {color:'red',y: 'left',});
        }
        this.loadingverificacion = false;
      },
      async buscarEstudiante() {
        if (!this.filtroestudiante || this.filtroestudiante.trim().length < 4) {
          this.msg = 'Ingrese al menos 4 dígitos del CI para buscar.';
          return;
        }
        this.msg = '';
        this.estudianteEncontrado = false;
        this.estudianteNoEncontrado = false;
        this.estudiantes = [];
        this.input.estudiante = '';
        this.input.num_doc = '';
        this.input.direccion = '';

        await this.$refs.abuscarpersona.asyncfindestudiante(false, this.filtroestudiante, this);

        if (this.estudiantes.length > 0) {
          const exacto = this.estudiantes.find(e => String(e.num_doc) === String(this.filtroestudiante));
          this.getDatos(exacto || this.estudiantes[0]);
        } else {
          this.estudianteNoEncontrado = true;
        }
        this.busquedaRealizada = true;
      },
      async getUsuario() {
        let id = this.$store.state.DATA_USER.id;
        const sql = gql`query{getUsuario(id: ${id}) { id firma ${''}}}`;
        let res = await this.$apollo.query({ query: sql }).then(({ data }) => data);
        const u = res.getUsuario;

        let canvas = document.getElementById('canvasrecibo');
        let ctx = canvas.getContext('2d');
        const vue = this;
        this.firma = null;
        if(u.firma){
            this.firma = new Image();
            this.firma.setAttribute('crossorigin', 'anonymous');
            this.firma.addEventListener('load', function() {
                ctx.drawImage(vue.firma, 950, 800,250, 250);
            }, false);
            this.firma.src = this.firma_path + u.firma;
        }
      },
      async getsedes(){
        this.xsedes = [];
        var sql = gql`query{getSedes{id,nombre,unidad_negocio}}`;
        let res = await this.$apollo.query({query: sql}).then((data) => data).catch((error) => error);
        if(res && res.data) {
            let idsede = this.$store.state.CARGO.sede.id;
            let filtrado = res.data.getSedes.filter(x=>x.id != idsede);
            this.xsedes = filtrado;
        }
      },
      getDatos(est){
        if (!est) est = typeof this.filtroestudiante === 'object' ? this.filtroestudiante : null;
        if (est) {
            let nombre = `${est.nombres||''} ${est.pri_apellido||''} ${est.seg_apellido||''}`;
            nombre = nombre.replace('null','').trim();
            this.input.estudiante = nombre;
            this.input.num_doc = est.num_doc;
            if(est.domicilio) this.input.direccion = est.domicilio;
            this.input.telefono = est.telefono1;
            this.estudianteEncontrado = true;
            this.estudianteNoEncontrado = false;
        }
      },
      cargarfondo(){
          if( this.fondo && this.input.recibo_fondo ){
              this.cargardatos();
              return;
          }
          var canvas = document.getElementById('canvasrecibo');
          var ctx = canvas.getContext('2d');
          const vue = this;
          this.fondo = new Image();
          this.fondo.setAttribute('crossorigin', 'anonymous');

        let unidad = this.$store.state.CARGO.sede.unidad_negocio;
        if(unidad==10){
            this.fondo.src = "img/recibo-blank-dbs.jpg";
            this.input.recibo_fondo = "img/recibo-blank-dbs.jpg";
        }else if(unidad==15){
            this.fondo.src = "img/recibo-blank-upi.jpg";
            this.input.recibo_fondo = "img/recibo-blank-upi.jpg";
        }else{
            this.fondo.src = "img/recibo-blank.jpg";
            this.input.recibo_fondo = "img/recibo-blank.jpg";
        }

          this.fondo.addEventListener('load', function() {
              ctx.drawImage(vue.fondo, 0, 0,1280, 1080);
              vue.cargardatos();
            }, false);
      },
      cargardatos(){
          this.validarextensionci();
        var canvas = document.getElementById('canvasrecibo');
        var ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if( this.fondo ) ctx.drawImage(this.fondo, 0, 0,1280, 1080);
        if( this.firma ) ctx.drawImage(this.firma, 950, 800,250, 250);

        const fs = this.input.fecha_pago.split('/');
        const d = fs[0];
        const m = fs[1];
        const a = fs[2];

        ctx.font = "27px Arial";
        ctx.textAlign = "center";
        ctx.fillText(""+this.input.lugar, (canvas.width/2)-490, 320+60);
        ctx.fillText(""+d, (canvas.width/2)-350, 320+60);
        ctx.fillText(""+m, (canvas.width/2)-275, 320+60);
        ctx.fillText(""+a, (canvas.width/2)-205, 320+60);

        ctx.font = "38px Arial";
        ctx.fillText("N° 000"+this.numero, (canvas.width/2), 340+70);

        ctx.font = "27px Arial";
        ctx.fillText(''+this.input.monto, (canvas.width/2)+400, 280+50);
        ctx.fillText('_____', (canvas.width/2)+410, 320+60);

        let estudent = this.input.estudiante;
        if(!estudent) {
          const fe = this.filtroestudiante;
          estudent = typeof fe === 'object' && fe ? `${fe.nombres||''} ${fe.pri_apellido||''} ${fe.seg_apellido||''}`.replace('null','').trim() : fe;
        }

        let doc = this.input.num_doc;
        if(!doc && this.filtroestudiante && typeof(this.filtroestudiante) == 'string'){
          doc = this.filtroestudiante
        }

        ctx.fillText(''+estudent, (canvas.width/2)-40, 443+84);
        ctx.fillText(''+doc, (canvas.width/2)+410, 443+84);
        ctx.fillText(''+this.input.direccion, (canvas.width/2)-170, 506+95);
        ctx.fillText(''+this.input.zona, (canvas.width/2)-10, 506+95);
        if( this.input.nro_factura ){
            ctx.fillStyle = "#ffffff";
            ctx.fillText('____', (canvas.width/2)+389, 505+95);
            ctx.font = "bold 22px verdana, sans-serif";
            ctx.fillStyle = "#1A2D60";
            ctx.fillText('Fact.:', (canvas.width/2)+390, 509+95);
            ctx.font = "27px Arial";
            ctx.fillStyle = "#000000";
            ctx.fillText(''+this.input.nro_factura, (canvas.width/2)+490, 506+95);
            ctx.fillText(''+this.input.celular, (canvas.width/2)+235, 506+95);
        }else{
            ctx.fillText(''+this.input.celular, (canvas.width/2)+360, 506+95);
        }
        ctx.fillText(''+this.input.concepto, (canvas.width/2)+70, 557+107);
        ctx.fillText(''+this.input.forma_pago, (canvas.width/2)+60, 614+117);
        if( this.input.monto ){
            ctx.fillText(''+this.input.monto+' Bs.', (canvas.width/2)-430, 664+125);
        }
        if( this.input.saldo ) ctx.fillText(''+this.input.saldo+' Bs.', (canvas.width/2)-130, 664+125);
        ctx.fillText(''+this.input.num_cuota, (canvas.width/2)+150, 664+125);
        ctx.fillText(''+this.input.proximo_pago, (canvas.width/2)+490, 664+125);
        ctx.fillText(''+this.input.asesor, (canvas.width/2)-250, 710+135);
        ctx.fillText(''+this.input.emisor, (canvas.width/2)+350, 710+135);
        ctx.save();

        ctx.font = "bold 20px verdana, sans-serif";
        ctx.fillStyle = "#08235b";
        ctx.fillText('Lic. '+this.input.emisor, (canvas.width/2)+410, 1045);
        ctx.restore();

        this.previewCertificado( canvas );
    },
    previewCertificado( canvas ){
        var modalsize = document.getElementById("content-preview").getBoundingClientRect();
        var a = (((modalsize.width+160) * 100) / canvas.width)/100;
        var destinationCanvas = document.getElementById("myCanvasview");
        var destCtx = destinationCanvas.getContext('2d');
        destinationCanvas.width = canvas.width;
        destinationCanvas.height = canvas.height/2;
        destCtx.scale(a, 0.5);
        destCtx.drawImage(canvas, 0, 0);
    },
    limpiarDatos(){
        this.generado = false;
        this.filtroestudiante = '';
        this.estudianteEncontrado = false;
        this.estudianteNoEncontrado = false;
        this.busquedaRealizada = false;
        this.estudiantes = [];
        for (var propName in this.input) {
            if( typeof(this.input[propName]) == 'string') this.input[propName] = '';
            else this.input[propName] = null;
        }
        this.fechahoy();
        this.fechaproximopago();
        this.getcurrentusuario();
    },
    validar(e){
        e.preventDefault();
        if(!this.cajaabierta){
            this.$root.$notify.show(`No hay caja aperturada`, {color:'red',y: 'right',});
            return;
        }
        if( this.generando ) return;
        window.localStorage.setItem( 'recibocacheado',JSON.stringify(this.input) );
        window.console.log('>>>',this.input);
        this.msg = '';
        let xinput = window.Object.assign( {},this.input );
        xinput.fecha_pago = this.voltearfecha( xinput.fecha_pago );
        xinput.proximo_pago = this.voltearfecha( xinput.proximo_pago );
        let errores = '';
        for (var propName in xinput) {
            if (!xinput[propName] || xinput[propName] === null || xinput[propName] === undefined) { delete xinput[propName]; }
            else {
                if( (typeof(xinput[propName]) == 'string' && propName != 'num_cuota' ) && xinput[propName].length < 5 ){
                    errores += `El campo ${propName} debe ser de al menos 5 caracteres.\n\n`;
                }
                if( propName == 'proximo_pago' && !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(xinput[propName]) ){
                    errores += `El campo ${propName} no cumple el patron: dd/mm/aaaa.\n\n`;
                }
            }
        }

        let estudent = xinput.estudiante;
        if(!estudent) {
          const fe = this.filtroestudiante;
          estudent = typeof fe === 'object' && fe ? `${fe.nombres||''} ${fe.pri_apellido||''} ${fe.seg_apellido||''}`.replace('null','').trim() : fe;
        }
        xinput.estudiante = estudent;

        if( !estudent ) errores += `Falta selecccionar al estudiante puede hacerlo por su carnet.\n\n`;
        if( !xinput.monto ) errores += `El campo monto debe tener un valor numerico y ser mayor a cero.\n\n`;
        if(!this.input.sede_destino) errores += `seleccione la sede destino\n\n`;
        if (isNaN(this.input.nro_factura)) errores += `El numero de factura debe ser un numero valido, sin letras ni espacios\n\n`;
        if (isNaN(this.input.num_cuota)) errores += `El numero de cuota debe ser un numero valido, sin letras ni espacios\n\n`;
        if (isNaN(this.input.celular)) errores += `El numero de celular debe ser un numero valido, sin letras ni espacios\n\n`;
        if (isNaN(this.input.telefono)) errores += `El numero de telefono debe ser un numero valido, sin letras ni espacios\n\n`;
        if(this.input.sede_destino) xinput.sede_destino_id = this.input.sede_destino.id;
        delete xinput.sede_destino;
        delete xinput.telefono;

        if( errores.length == 0 ){
            this.confirmar( xinput );
        }else{
            this.msg = 'ERRORES: '+errores;
        }
    },
    confirmar( xinput ){
        const vue = this;
        var message = {
            title:'Confirme que desea proceder',
            body: '<br/>NOTA: El numero de recibo se asigna despues de generar.<br/>'
        };
        var options = {html:true,okText: 'Proceder',cancelText: 'Cancelar',reverse:true,type:'classic',
            verification: 'pagado',verificationHelp: 'Escriba "[+:verification]" para proceder.',loader: true,};
            this.$dialog.confirm(message,options)
            .then(function(dialog) {
                vue.generarrecibo( xinput );
                dialog.close();
            }).catch(function() {});
    },
    generarrecibo( xinput ){
        this.generando = true;
        const vue = this;
        var sql = gql`mutation{ generarRecibo(input:${window.stringifyObject(xinput)}){ ok recibo{id,nro_factura,direccion,zona,codigo,nro,lugar,num_doc,estudiante,telefono,celular,concepto,forma_pago,monto,saldo,num_cuota,fecha_registro,fecha_pago,proximo_pago,asesor,emisor},errors{message}} }`;

        this.$apollo.mutate({
            mutation: sql,
        }).then(({ data: { generarRecibo } }) => {
            var res = generarRecibo;
            if (res.ok) {
                vue.generado = true;
                vue.numero = res.recibo.nro;
                vue.cargardatos();
                setTimeout(function(){
                    vue.guardarpdf(res.recibo);
                },1000)
            } else {
                var errores = JSON.stringify(res.errors);
                var obj = JSON.parse(errores);
                var vals = '';
                obj.forEach(function (k) { vals += Object.values(k) + '-\n\t'; });
                vue.msg = vals;
                vue.generando = false;
            }
        }).catch((error) => {
            let err = JSON.parse( JSON.stringify(error) );
            vue.msg = 'SERVER: '+err.graphQLErrors[0].message.message;
            vue.generando = false;
            vue.generado = false;
        });
    },
    async guardarpdf(recibo){
        var canvas = document.getElementById("canvasrecibo");
        let width = canvas.width;
        let height = canvas.height;
        var pdf;

        let estudent = this.input.estudiante;
        if(!estudent) {
          const fe = this.filtroestudiante;
          estudent = typeof fe === 'object' && fe ? `${fe.nombres||''} ${fe.pri_apellido||''} ${fe.seg_apellido||''}`.replace('null','').trim() : fe;
        }

        var nombre = estudent+'-000'+this.numero;

        if (width > height) { pdf = new window.jsPDF('l', 'px', [width, height]); }
        else { pdf = new window.jsPDF('p', 'px', [height, width]); }

        width = pdf.internal.pageSize.getWidth();
        height = pdf.internal.pageSize.getHeight();
        pdf.addImage(canvas, 'JPG', 0, 0, width, height);

        const base64String = pdf.output('datauristring');
        await this.guardarServer(base64String,recibo);
        pdf.save(nombre + '.pdf');
        this.generando = false;
    },
    async guardarServer(b64,recibo){
        let partes = b64.split("base64,");
        let cadenaBase64 = partes[1];

        let data = JSON.stringify({
            "app": process.env.VUE_APP_SERVERFILES_APP,
            "base64": cadenaBase64
        });

        let config = {
            method: 'post',
            url: process.env.VUE_APP_SERVERFILES,
            headers: {
                'Content-Type': 'application/json'
            },
            data : data
        };

        let res = await xaxios.request(config).then((response) => response.data).catch((error) => error);
        window.console.log(res,recibo);

        let configSave = {
            method: 'put',
            url: `${process.env.VUE_APP_API_REST}/api/v1/esam/recibo_antiguo/addarchivo`,
            headers: {
                'Content-Type': 'application/json'
            },
            data : {
                recibo_antiguo_id: recibo.id,
                archivo_id: res.id
            }
        };

        let rese = await xaxios.request(configSave).then((response) => response.data).catch((error) => error);
        window.console.log(rese);
    }
  },
  async mounted(){
      await this.getUsuario();
      this.fechahoy();
      this.fechaproximopago();
      this.getcurrentusuario();
      this.getsedes();
      this.verificar();
      let xr = window.localStorage.getItem('recibocacheado');
      if( xr ) {
        this.input = JSON.parse( xr );
        if( !this.input.recibo_fondo ) this.cargarfondo();
      }
      this.input.sede_id = this.$store.state.CARGO.idsede;
      this.input.sede_destino = null;
      this.limpiarDatos();
  }
}
</script>
